/* NONEUR-SNAP-1 / EUR-SNAP-1: theme progress bar under the Snap Cart header.
 * Snap's reward bar goes wrong after cart updates (non-EUR: wrongly converted discount; EUR: a
 * stale discount after a code is applied and removed), so pb-snap-shipping-thresholds keeps it off
 * in every market and this bar reads the discounted total from /cart.js. Same threshold table (window.__pbSnapShippingRules). Unknown, empty, over-limit
 * or unreadable carts show nothing. Never changes the cart or the buyer's shipping choice. */
(() => {
  'use strict';

  const context = document.getElementById('pb-snap-free-ship-context');
  const rules = window.__pbSnapShippingRules;
  if (!context || !rules || window.__pbSnapFreeShip) return;
  let config;
  try { config = JSON.parse(context.textContent); } catch (error) { return; }
  const country = String(config.country || '').toUpperCase();
  const currency = String(config.currency || '').toUpperCase();
  const rule = rules.resolveRule(country, currency);
  if (!rule.supported || !rule.matches) return;
  const threshold = Math.round(rule.amount * 100);
  const max = rule.max ? Math.round(rule.max * 100) : null;

  // Reviewed cart-page copy (pb-free-shipping-progress); cs/hu/ro/no drafted 2026-10-27 and approved;
  // pt/el/sk/lt/lv/hr/bg (EUR markets) drafted 2026-10-30 for Owner review.
  const COPY = {
    below: {
      de: 'Noch {amount} bis zum kostenlosen Versand.',
      nl: 'Nog {amount} tot gratis verzending.',
      fr: 'Plus que {amount} pour la livraison gratuite.',
      es: 'Te faltan {amount} para el envío gratuito.',
      it: 'Ancora {amount} per la spedizione gratuita.',
      pl: 'Brakuje {amount} do darmowej dostawy.',
      sv: '{amount} kvar till fri frakt.',
      fi: '{amount} puuttuu ilmaisesta toimituksesta.',
      da: '{amount} fra gratis fragt.',
      cs: 'Do dopravy zdarma zbývá {amount}.',
      hu: 'Még {amount} az ingyenes szállításig.',
      ro: 'Mai sunt {amount} până la livrarea gratuită.',
      no: '{amount} igjen til gratis frakt.',
      pt: 'Faltam {amount} para os portes grátis.',
      el: 'Ακόμα {amount} για δωρεάν αποστολή.',
      sk: 'Do dopravy zadarmo zostáva {amount}.',
      lt: 'Iki nemokamo pristatymo trūksta {amount}.',
      lv: 'Līdz bezmaksas piegādei atlikuši {amount}.',
      hr: 'Do besplatne dostave nedostaje još {amount}.',
      bg: 'Остават {amount} до безплатна доставка.',
      en: "You're {amount} away from free shipping."
    },
    done: {
      de: 'Kostenloser Versand ist freigeschaltet.',
      nl: 'Gratis verzending is vrijgespeeld.',
      fr: 'Livraison gratuite débloquée.',
      es: 'Envío gratuito desbloqueado.',
      it: 'Spedizione gratuita sbloccata.',
      pl: 'Darmowa dostawa odblokowana.',
      sv: 'Fri frakt upplåst.',
      fi: 'Ilmainen toimitus avattu.',
      da: 'Gratis fragt låst op.',
      cs: 'Máte dopravu zdarma.',
      hu: 'A szállítás ingyenes.',
      ro: 'Livrarea este gratuită.',
      no: 'Gratis frakt er låst opp.',
      pt: 'Portes grátis desbloqueados.',
      el: 'Η αποστολή είναι δωρεάν.',
      sk: 'Doprava je zadarmo.',
      lt: 'Pristatymas nemokamas.',
      lv: 'Piegāde ir bez maksas.',
      hr: 'Dostava je besplatna.',
      bg: 'Доставката е безплатна.',
      en: 'Free shipping unlocked.'
    }
  };
  const locale = COPY.below[config.locale] ? config.locale : 'en';
  // Format like the drawer's own prices: the shop's money format rendered by Liquid for 123456789
  // cents gives the separators ("€1.234.567,89", "£1,234,567.89", "1 234 568 Ft"). Without it,
  // fall back to the browser's formatting for the locale.
  const sample = String(config.sample || '');
  const sampleNumber = (sample.match(/\d[\d.,'\u00a0\u202f ]*\d/) || [])[0];
  let intl;
  try {
    intl = new Intl.NumberFormat(locale + '-' + country, { style: 'currency', currency });
  } catch (error) {
    intl = new Intl.NumberFormat('en', { style: 'currency', currency });
  }
  const money = {
    format: (amount) => {
      if (!sampleNumber) return intl.format(amount);
      const cents = Math.round(amount * 100);
      const decimals = sampleNumber.replace(/\D/g, '').length === 9;
      const decimalMark = decimals ? sampleNumber[sampleNumber.length - 3] : '';
      const group = ((decimals ? sampleNumber.slice(0, -3) : sampleNumber).match(/\D/) || [''])[0];
      const [whole, fraction] = (decimals ? (cents / 100).toFixed(2) : String(Math.round(cents / 100))).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);
      return sample.replace(sampleNumber, decimals ? grouped + decimalMark + fraction : grouped);
    }
  };

  const TRUCK = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 6h12v10H2zM14 10h4l3 3v3h-7z"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>';

  // Tiers on one bar: free shipping always; others (the gift with purchase) join through
  // window.__pbCartProgress.addTier. Each tier has its own amount from /cart.js, because what
  // qualifies differs per tier, and its own line of text: the bar never merges two promises.
  const tiers = [{
    id: 'shipping',
    threshold,
    icon: TRUCK,
    classify: (cart) => {
      if (cart.items.some((item) => item.requires_shipping === false)) return null;
      // A rule with an upper limit offers no rate above it, so promise nothing there.
      if (max !== null && cart.total_price > max) return null;
      return cart.total_price;
    },
    text: (amount) => (amount >= threshold
      ? COPY.done[locale]
      : COPY.below[locale].replace('{amount}', money.format((threshold - amount) / 100)))
  }];

  const node = document.createElement('div');
  node.className = 'pb-snap-free-ship';
  node.hidden = true;
  const lines = document.createElement('div');
  lines.className = 'pb-cart-progress__lines';
  lines.setAttribute('role', 'status');
  lines.setAttribute('aria-live', 'polite');
  const track = document.createElement('div');
  track.className = 'pb-cart-progress__track';
  track.setAttribute('aria-hidden', 'true'); // the lines above carry the information
  const fill = document.createElement('div');
  fill.className = 'pb-cart-progress__fill';
  track.append(fill);
  node.append(lines, track);

  let sequence = 0;
  let shown = '';
  let signature = null;
  let timer = null;
  let lastCart = null;

  const render = (cart) => {
    node.removeAttribute('aria-busy');
    const usable = !!cart && !!cart.item_count && cart.currency === currency;
    const active = (usable ? tiers : [])
      .map((tier) => ({ tier, amount: tier.classify(cart) }))
      .filter((row) => row.amount !== null)
      .sort((a, b) => a.tier.threshold - b.tier.threshold);
    const key = active.map((row) => row.tier.id + ':' + row.amount).join('|');
    if (key === shown) return;
    shown = key;
    node.dataset.pbSnapFreeShip = !active.length ? 'hidden' : active.every((row) => row.amount >= row.tier.threshold) ? 'done' : 'below';
    node.hidden = !active.length;
    if (!active.length) return;

    // Each tier fills its own stretch of the bar, up to a marker placed by its threshold.
    const top = active[active.length - 1].tier.threshold;
    let previous = 0;
    let filled = 0;
    track.querySelectorAll('.pb-cart-progress__marker').forEach((marker) => marker.remove());
    active.forEach(({ tier, amount }) => {
      const stretch = tier.threshold - previous;
      filled += Math.max(0, Math.min(1, (amount - previous) / stretch)) * stretch;
      const marker = document.createElement('span');
      marker.className = 'pb-cart-progress__marker';
      marker.style.insetInlineStart = (tier.threshold / top) * 100 + '%';
      marker.innerHTML = tier.icon;
      marker.toggleAttribute('data-done', amount >= tier.threshold);
      track.append(marker);
      previous = tier.threshold;
    });
    fill.style.width = (filled / top) * 100 + '%';
    lines.replaceChildren(...active.map(({ tier, amount }) => {
      const line = document.createElement('p');
      line.textContent = tier.text(amount);
      return line;
    }));
  };

  const head = () => document.querySelector('#wizz4-cart-drawer .w4-cart-head');

  // Directly under the drawer header, where Snap's own bar sat, and outside the scrolling body.
  const place = () => {
    const anchor = head();
    if (!anchor || !anchor.parentNode) return false;
    if (node.previousElementSibling !== anchor) anchor.after(node);
    return true;
  };

  const refresh = async () => {
    const id = ++sequence;
    node.setAttribute('aria-busy', 'true');
    let cart = null;
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (response.ok) cart = await response.json();
    } catch (error) { /* show nothing rather than a stale amount */ }
    if (id !== sequence) return;
    lastCart = cart;
    render(cart);
    place();
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(refresh, 200);
  };

  // Only the checkout total and the item count can change the bar; ignore other drawer
  // updates (timers, animations) and this bar's own changes.
  const readSignature = () => {
    const drawer = document.getElementById('wizz4-cart-drawer');
    if (!drawer) return null;
    const link = drawer.querySelector('#wizz4-cart-link');
    const title = head();
    return (link ? link.textContent : '') + '|' + (title ? title.textContent : '');
  };

  const onMutation = () => {
    if (!place()) return;
    const next = readSignature();
    if (next === signature) return;
    signature = next;
    schedule();
  };

  const observer = new MutationObserver((records) => {
    if (records.every((record) => node.contains(record.target))) return;
    onMutation();
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  document.addEventListener('wizz:cart-drawer-visibility', () => { signature = null; onMutation(); });

  window.__pbSnapFreeShip = { refresh, destroy: () => { observer.disconnect(); node.remove(); } };
  window.__pbCartProgress = {
    format: (cents) => money.format(cents / 100),
    addTier: (tier) => {
      if (tiers.some((existing) => existing.id === tier.id)) return;
      tiers.push(tier);
      shown = '';
      if (lastCart) render(lastCart);
    }
  };
  document.dispatchEvent(new CustomEvent('pb:cart-progress-ready'));
})();
